import { Portal, TextField } from "@material-ui/core";
import { RadioButtonChecked, RadioButtonUnchecked } from "@material-ui/icons";
import { StyledAutoComplete } from "../styles/MaterialGroopTheme";
import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  sub,
  subQuarters,
} from "date-fns";
import React, { ReactNode, useCallback, useState } from "react";
import { DatePickerRangeDTO, DatePickerRangeId } from "groopit-js/client";
import {
  FilterPickerVariants,
  getPopperComponent,
  isOpenByDefault,
} from "./FilterPicker";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./DatePicker.css";
import { useDebounce } from "react-use";
import { AutocompleteRenderInputParams } from "@material-ui/lab";

export const GetDateRange = (
  id: DatePickerRangeId,
  startDate?: Date,
  endDate?: Date,
) => {
  const now = new Date();
  switch (id) {
    case DatePickerRangeId.LastDay:
      return new DatePickerRangeDTO({
        id,
        startDate: sub(now, { hours: 24 }),
        endDate: now,
      });
    case DatePickerRangeId.CurrentWeek:
      return new DatePickerRangeDTO({
        id,
        startDate: startOfWeek(now),
      });
    case DatePickerRangeId.PreviousWeek:
      return new DatePickerRangeDTO({
        id,
        startDate: startOfWeek(sub(now, { weeks: 1 })),
        endDate: endOfWeek(sub(now, { weeks: 1 })),
      });
    case DatePickerRangeId.CurrentMonth:
      return new DatePickerRangeDTO({
        id,
        startDate: startOfMonth(now),
      });
    case DatePickerRangeId.PreviousMonth:
      return new DatePickerRangeDTO({
        id,
        startDate: startOfMonth(sub(now, { months: 1 })),
        endDate: endOfMonth(sub(now, { months: 1 })),
      });
    case DatePickerRangeId.CurrentQuarter:
      return new DatePickerRangeDTO({
        id,
        startDate: startOfQuarter(now),
      });
    case DatePickerRangeId.PreviousQuarter:
      return new DatePickerRangeDTO({
        id,
        startDate: startOfQuarter(subQuarters(now, 1)),
        endDate: endOfQuarter(subQuarters(now, 1)),
      });
    case DatePickerRangeId.CurrentYear:
      return new DatePickerRangeDTO({
        id,
        startDate: startOfYear(now),
      });
    case DatePickerRangeId.PreviousYear:
      return new DatePickerRangeDTO({
        id,
        startDate: startOfYear(sub(now, { years: 1 })),
        endDate: endOfYear(sub(now, { years: 1 })),
      });
    case DatePickerRangeId.Custom:
      return new DatePickerRangeDTO({
        id,
        startDate,
        endDate,
      });
  }
};

interface DatePickerProps {
  selectedOption?: DatePickerRangeDTO;
  disabled?: boolean;
  onChange: (selectedOption?: DatePickerRangeDTO) => void;
  variant: FilterPickerVariants;
}

export const DatePicker = (props: DatePickerProps) => {
  const { disabled, variant, selectedOption, onChange } = {
    ...props,
  };
  const [open, setOpen] = useState(false);

  const getOptionLabel = (option: DatePickerRangeDTO | null): string => {
    if (option == null) {
      return "All dates";
    } else {
      switch (option.id) {
        case DatePickerRangeId.LastDay:
          return "Last 24 hours";
        case DatePickerRangeId.CurrentWeek:
          return "Current week";
        case DatePickerRangeId.PreviousWeek:
          return "Previous week";
        case DatePickerRangeId.CurrentMonth:
          return "Current month";
        case DatePickerRangeId.PreviousMonth:
          return "Previous month";
        case DatePickerRangeId.CurrentQuarter:
          return "Current quarter";
        case DatePickerRangeId.PreviousQuarter:
          return "Previous quarter";
        case DatePickerRangeId.CurrentYear:
          return "Current year";
        case DatePickerRangeId.PreviousYear:
          return "Previous year";
        case DatePickerRangeId.Custom:
          return "Custom date range";
      }
    }
  };

  const possibleDates: (DatePickerRangeDTO | null)[] = [
    null,
    GetDateRange(
      DatePickerRangeId.Custom,
      selectedOption?.startDate,
      selectedOption?.endDate,
    ),
    GetDateRange(DatePickerRangeId.LastDay),
    GetDateRange(DatePickerRangeId.CurrentWeek),
    GetDateRange(DatePickerRangeId.PreviousWeek),
    GetDateRange(DatePickerRangeId.CurrentMonth),
    GetDateRange(DatePickerRangeId.PreviousMonth),
    GetDateRange(DatePickerRangeId.CurrentQuarter),
    GetDateRange(DatePickerRangeId.PreviousQuarter),
    GetDateRange(DatePickerRangeId.CurrentYear),
    GetDateRange(DatePickerRangeId.PreviousYear),
  ];

  const getInputProps = useCallback(
    (inputProps: AutocompleteRenderInputParams["InputProps"]) => {
      switch (variant) {
        case "outlined":
          return {
            ...inputProps,
          };
        default:
          return {
            ...inputProps,
            disableUnderline: true,
          };
      }
    },
    [variant],
  );

  // When using the inline variant there is a transition
  // effect that displays the popover that contains the
  // autocomplete. If we default the autocomplete to
  // open right away, it's position is wrong. So we delay
  // the opening slightly to give the animation time to
  // settle.
  useDebounce(
    () => {
      if (isOpenByDefault(variant)) {
        setOpen(true);
      }
    },
    100,
    [variant],
  );

  return (
    <>
      <StyledAutoComplete
        PopperComponent={(popperProps) => getPopperComponent(popperProps)}
        fullWidth
        disabled={disabled}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        openOnFocus
        getOptionSelected={(option, value) => option?.id === value?.id}
        getOptionLabel={getOptionLabel}
        renderInput={(params) => (
          <TextField
            {...params}
            InputProps={getInputProps(params.InputProps)}
            label={selectedOption ? "Within" : "All dates"}
            variant={variant === "outlined" ? "outlined" : "filled"}
          />
        )}
        renderOption={(option, state) =>
          option ? (
            <div className="space-x-2 truncate">
              {state.selected ? (
                <RadioButtonChecked />
              ) : (
                <RadioButtonUnchecked />
              )}
              <span>{getOptionLabel(option)}</span>
            </div>
          ) : (
            <div className="italic">{getOptionLabel(null)}</div>
          )
        }
        options={possibleDates}
        value={selectedOption || null}
        onChange={(_, value) => onChange(value ?? undefined)}
      />
      {selectedOption?.id === "Custom" && (
        <div className="flex justify-between">
          <ReactDatePicker
            className="form-control"
            selected={selectedOption.startDate}
            onChange={(start: Date) =>
              onChange(
                new DatePickerRangeDTO({
                  id: DatePickerRangeId.Custom,
                  startDate: start,
                  endDate: selectedOption.endDate,
                }),
              )
            }
            selectsStart
            startDate={selectedOption.startDate}
            endDate={selectedOption.endDate}
            placeholderText="Select a start date"
            isClearable
            popperContainer={(props: { children: ReactNode[] }) => (
              <Portal>{props.children}</Portal>
            )}
          />
          <ReactDatePicker
            className="form-control"
            selected={selectedOption.endDate}
            onChange={(end: Date) =>
              onChange(
                new DatePickerRangeDTO({
                  id: DatePickerRangeId.Custom,
                  startDate: selectedOption.startDate,
                  endDate: end,
                }),
              )
            }
            selectsEnd
            startDate={selectedOption.startDate}
            minDate={selectedOption.endDate}
            endDate={selectedOption.endDate}
            placeholderText="Select an end date"
            isClearable
            popperContainer={(props: { children: ReactNode[] }) => (
              <Portal>{props.children}</Portal>
            )}
          />
        </div>
      )}
    </>
  );
};
